import { memo, useCallback, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import dayjs from 'dayjs';

import { displayCount, displayDuration, displayTimeAgo } from 'utils';
import { Card, MembersOnlyBadge } from 'components';
import { ArrowRightIcon, CameraIcon, ClockIcon, EyeCrossedIcon, PlayCircleNewIcon } from 'components/Icon';

import styles from './EventCard.Past.module.scss';

export type PastEventCardProps = {
  poster?: string;
  eventCode: string;
  eventName: string;
  startDate: string;
  duration: number;
  recordingWatchCount: number;
  channelName: string;
  isMembersOnly?: boolean;
  hasAccess?: boolean;
  isHidden?: boolean;
  onClick?(): void;
};

const displayStartDate = (date: string) => displayTimeAgo(date);
const displayWatchCount = (watchCount: number) => `${displayCount(watchCount)} views`;

const Duration = memo<{ minutes: number }>(({ minutes }) => {
  const value = useRef(dayjs.duration({ minutes: minutes || 1 }));

  useEffect(() => {
    value.current = dayjs.duration({ minutes: minutes || 1 });

    const interval = setInterval(() => {
      value.current = dayjs.duration({ minutes: minutes || 1 });
    }, 1_000);

    return () => {
      clearInterval(interval);
    };
  }, [minutes]);

  return <span>{displayDuration(value.current.asMilliseconds())}</span>;
});

const PastEventCard = ({
  eventCode,
  eventName,
  startDate,
  duration,
  recordingWatchCount,
  poster,
  channelName,
  isMembersOnly = false,
  hasAccess = false,
  isHidden = false,
  onClick
}: PastEventCardProps): JSX.Element => {
  const [posterLoadFailed, setPosterLoadFailed] = useState(!poster);

  const handleOnPosterLoadFailed = useCallback(() => setPosterLoadFailed(true), []);

  const handleOnClick = () => {
    onClick?.();
  };

  return (
    <Card className={cx(styles.container, { [styles.hidden]: isHidden })} onClick={handleOnClick}>
      <div className={styles.poster}>
        <div className={styles.duration}>
          <ClockIcon />
          <Duration minutes={duration} />
        </div>

        {isHidden ? (
          <div className={styles.hiddenBadge}>
            <EyeCrossedIcon width={36} height={36} />
          </div>
        ) : (
          <PlayCircleNewIcon className={styles.play} />
        )}
        {posterLoadFailed || !poster ? (
          <div className={styles.placeholder}>
            <CameraIcon />
          </div>
        ) : (
          <img className={styles.image} src={poster} alt={eventName} onError={handleOnPosterLoadFailed} />
        )}
        {isMembersOnly && <MembersOnlyBadge className={styles.membersOnlyBadge} hasAccess={hasAccess} />}
      </div>
      <div className={styles.content}>
        <div className={styles.title}>{eventName}</div>
        <div className={styles.channel}>@{channelName}</div>
        <ul className={styles.stats}>
          <li>{displayStartDate(startDate)}</li>
          <li>{displayWatchCount(recordingWatchCount)}</li>
        </ul>
        <div className={styles.cta}>
          <span className="mr-4">Watch now</span>
          <ArrowRightIcon />
        </div>
      </div>
    </Card>
  );
};

export default memo(PastEventCard);
